import { useState } from 'react';
import useAutoSave from '../../hooks/useAutoSave';
import AutoSaveStatus from '../Global/AutoSaveStatus';
import './scss/OAuthConfiguration.scss';

const OAuthConfiguration = ({ config, onUpdate, loading }) => {
  const [oauth, setOAuth] = useState(config?.oauth || {
    enableGoogleAuth: false,
    enableGithubAuth: false,
    googleClientId: '',
    githubClientId: ''
  });

  // Validation function
  const validateOAuth = (data) => {
    const errors = {};
    const oauthData = data.oauth || data;

    if (oauthData.googleClientId) {
      // Basic validation for Google Client ID format
      if (!oauthData.googleClientId.includes('.googleusercontent.com')) {
        errors.googleClientId = 'Invalid Google Client ID format';
      }
    }

    if (oauthData.githubClientId) {
      // Basic validation for GitHub Client ID format
      if (oauthData.githubClientId.length < 10) {
        errors.githubClientId = 'GitHub Client ID seems too short';
      }
    }

    return errors;
  };

  // Auto-save hook
  const { autoSaving, lastSaved, errors, autoSave, clearFieldError } = useAutoSave(
    (data) => onUpdate(data),
    validateOAuth
  );

  const handleInputChange = (field, value) => {
    const updatedOAuth = {
      ...oauth,
      [field]: value
    };
    setOAuth(updatedOAuth);

    // Clear error for this field
    clearFieldError(field);

    // Auto-save the changes
    autoSave({ oauth: updatedOAuth });
  };



  const getRedirectUrls = () => {
    const domain = config?.domain?.customDomain || `${config?.subdomain || config?.slug}.zoobbe.com`;
    return {
      google: `https://${domain}/auth/oauth/callback`,
      github: `https://${domain}/auth/oauth/callback`
    };
  };

  const redirectUrls = getRedirectUrls();

  return (
    <div className="oauth-configuration">
      <div className="section">
        <h3>OAuth Authentication</h3>
        <p className="section-description">
          Configure custom OAuth providers for your white label instance. Users will be able to sign in using these providers.
        </p>

        {errors.general && (
          <div className="error-banner">
            {errors.general}
          </div>
        )}

        <div className="oauth-provider">
          <div className="provider-header">
            <div className="provider-info">
              <h4>Google OAuth</h4>
              <p>Allow users to sign in with their Google accounts</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={oauth.enableGoogleAuth || false}
                onChange={(e) => handleInputChange('enableGoogleAuth', e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          {oauth.enableGoogleAuth && (
            <div className="provider-config">
              <div className="form-group">
                <label>Google Client ID</label>
                <input
                  type="text"
                  value={oauth.googleClientId || ''}
                  onChange={(e) => handleInputChange('googleClientId', e.target.value)}
                  placeholder="Your Google OAuth Client ID"
                  className={errors.googleClientId ? 'error' : ''}
                />
                {errors.googleClientId && <span className="error-text">{errors.googleClientId}</span>}
                <small>Get this from your Google Cloud Console OAuth 2.0 credentials</small>
              </div>

              <div className="form-group">
                <label>Authorized Redirect URI</label>
                <div className="redirect-uri">
                  <span className="uri-text">{redirectUrls.google}</span>
                  <button
                    type="button"
                    onClick={() => navigator.clipboard.writeText(redirectUrls.google)}
                    className="copy-btn"
                    title="Copy to clipboard"
                  >
                    <span className="material-symbols-outlined">content_copy</span>
                  </button>
                </div>
                <small>Add this URL to your Google OAuth app's authorized redirect URIs</small>
              </div>
            </div>
          )}
        </div>

        <div className="oauth-provider">
          <div className="provider-header">
            <div className="provider-info">
              <h4>GitHub OAuth</h4>
              <p>Allow users to sign in with their GitHub accounts</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={oauth.enableGithubAuth || false}
                onChange={(e) => handleInputChange('enableGithubAuth', e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          {oauth.enableGithubAuth && (
            <div className="provider-config">
              <div className="form-group">
                <label>GitHub Client ID</label>
                <input
                  type="text"
                  value={oauth.githubClientId || ''}
                  onChange={(e) => handleInputChange('githubClientId', e.target.value)}
                  placeholder="Your GitHub OAuth App Client ID"
                  className={errors.githubClientId ? 'error' : ''}
                />
                {errors.githubClientId && <span className="error-text">{errors.githubClientId}</span>}
                <small>Get this from your GitHub OAuth App settings</small>
              </div>

              <div className="form-group">
                <label>Authorization Callback URL</label>
                <div className="redirect-uri">
                  <span className="uri-text">{redirectUrls.github}</span>
                  <button
                    type="button"
                    onClick={() => navigator.clipboard.writeText(redirectUrls.github)}
                    className="copy-btn"
                    title="Copy to clipboard"
                  >
                    <span className="material-symbols-outlined">content_copy</span>
                  </button>
                </div>
                <small>Set this as the Authorization callback URL in your GitHub OAuth App</small>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="section">
        <h3>Setup Instructions</h3>
        <div className="instructions">
          <div className="instruction-item">
            <h4>Google OAuth Setup:</h4>
            <ol>
              <li>Go to <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer">Google Cloud Console</a></li>
              <li>Create a new project or select an existing one</li>
              <li>Enable the Google+ API</li>
              <li>Go to Credentials → Create Credentials → OAuth 2.0 Client IDs</li>
              <li>Add the redirect URI shown above</li>
              <li>Copy the Client ID and paste it above</li>
            </ol>
          </div>

          <div className="instruction-item">
            <h4>GitHub OAuth Setup:</h4>
            <ol>
              <li>Go to <a href="https://github.com/settings/developers" target="_blank" rel="noopener noreferrer">GitHub Developer Settings</a></li>
              <li>Click "New OAuth App"</li>
              <li>Fill in your app details</li>
              <li>Set the Authorization callback URL to the one shown above</li>
              <li>Copy the Client ID and paste it above</li>
            </ol>
          </div>
        </div>
      </div>

      <AutoSaveStatus
        autoSaving={autoSaving}
        lastSaved={lastSaved}
      />
    </div>
  );
};

export default OAuthConfiguration;
